import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Statistic, Select, Space, Spin, Typography, Divider, Empty, Tag, Button, Table, theme } from 'antd';
import {
  TrendingUp,
  TrendingDown,
  IndianRupee,
  CreditCard,
  Wallet,
  BarChart3,
  PieChart as PieIcon,
  Calendar,
  Building2,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  Download,
  FileText,
  Clock,
  Layers,
  Users
} from 'lucide-react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { fetchPerformanceSummary } from '../utils/api';
import { formatIndianCurrency, formatIndianNumber } from '../utils/numberToWords';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  DoughnutController,
  Title as ChartTitle,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  DoughnutController,
  ChartTitle,
  Tooltip,
  Legend,
  ArcElement
);

const { Title, Text } = Typography;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const Dashboard = ({ clients = [], invoices = [], facilities = [], config = {} }) => {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Default range: Last 12 Months
  const [filters, setFilters] = useState({
    startDate: dayjs().subtract(1, 'year').startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    facility: ''
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const summary = await fetchPerformanceSummary(filters);
      setData(summary);
    } catch (error) {
      console.error('Failed to load performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filters]);

  // --- Chart Data from Backend API ---
  const trendChartData = useMemo(() => {
    if (!data || !data.monthlyTrend) return null;

    const labels = data.monthlyTrend.map(item => `${MONTHS[item._id.month - 1]} ${item._id.year}`);

    return {
      labels,
      datasets: [
        {
          label: 'Revenue',
          data: data.monthlyTrend.map(item => item.revenue),
          backgroundColor: 'rgba(99, 102, 241, 0.5)',
          borderColor: 'rgb(99, 102, 241)',
          borderWidth: 2,
          type: 'bar',
          borderRadius: 6,
        },
        {
          label: 'Collection',
          data: data.monthlyTrend.map(item => item.collection),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.5)',
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          type: 'line',
          pointRadius: 4,
          pointBackgroundColor: 'rgb(34, 197, 94)',
        }
      ]
    };
  }, [data]);

  const collectionRate = data?.summary ? (data.summary.collection / data.summary.revenue * 100).toFixed(1) : 0;

  // --- Logic from Old Dashboard (Client-Side) ---

  const getFilteredInvoices = () => {
    const start = dayjs(filters.startDate);
    const end = dayjs(filters.endDate);

    return invoices.filter(inv => {
      if (inv.isCancelled) return false;
      const invDate = dayjs(inv.invoiceDate);
      const inRange = (invDate.isAfter(start) || invDate.isSame(start, 'day')) &&
        (invDate.isBefore(end) || invDate.isSame(end, 'day'));

      if (!inRange) return false;

      if (filters.facility) {
        // Facility filter is usually on the client object in the prop-based invoices
        const clientObj = typeof inv.client === 'object' ? inv.client : clients.find(c => c._id === inv.client || c.id === inv.client);
        return clientObj?.facilityName === filters.facility || clientObj?.facilityId === filters.facility || inv.facility === filters.facility;
      }

      return true;
    });
  };

  const filteredInvoices = getFilteredInvoices();

  const getInvoiceTypeData = () => {
    const adhocCount = filteredInvoices.filter(inv => inv.invoiceType === 'adhoc').length;
    const rentCount = filteredInvoices.filter(inv => inv.invoiceType === 'rent').length;
    const creditNoteCount = filteredInvoices.filter(inv => inv.invoiceType === 'credit_note').length;

    return {
      labels: ['Adhoc', 'Rent', 'Credit Notes'],
      datasets: [{
        data: [adhocCount, rentCount, creditNoteCount],
        backgroundColor: ['#f5576c', '#6366f1', '#f59e0b'],
        borderWidth: 0,
        hoverOffset: 4
      }]
    };
  };

  const getTopClients = () => {
    const clientMap = {};
    filteredInvoices.forEach(inv => {
      const clientId = typeof inv.client === 'object' ? (inv.client._id || inv.client.id) : inv.client;
      if (!clientId) return;

      const invoiceAmount = inv.totals?.total || 0;
      const receivedAmount = inv.receivedAmount || 0;
      const tdsAmount = inv.tdsPercent ? (inv.totals?.subtotal || 0) * (inv.tdsPercent / 100) : 0;

      if (!clientMap[clientId]) {
        const clientObj = typeof inv.client === 'object' ? inv.client : clients.find(c => c._id === clientId || c.id === clientId);
        clientMap[clientId] = {
          name: clientObj?.registeredName || clientObj?.name || 'Unknown Client',
          totalInvoiced: 0,
          totalReceived: 0,
          tdsDeducted: 0,
          outstanding: 0
        };
      }

      clientMap[clientId].totalInvoiced += invoiceAmount;
      clientMap[clientId].totalReceived += receivedAmount;
      clientMap[clientId].tdsDeducted += tdsAmount;

      // Matching old dashboard's TDS-aware outstanding logic
      const outstanding = inv.receivedAmount && inv.tdsPercent ? 0 : (invoiceAmount - receivedAmount);
      clientMap[clientId].outstanding += outstanding;
    });

    return Object.values(clientMap)
      .sort((a, b) => b.totalInvoiced - a.totalInvoiced)
      .map(client => ({
        ...client,
        collectionPercent: client.totalInvoiced > 0 ? Math.round((client.totalReceived / client.totalInvoiced) * 100) : 0
      }));
  };

  const exportTopClientsToExcel = () => {
    const topClientsData = getTopClients();
    const worksheet = XLSX.utils.json_to_sheet(topClientsData.map((c, i) => ({
      'S.No': i + 1,
      'Client Name': c.name,
      'Total Invoiced (₹)': c.totalInvoiced,
      'Total Received (₹)': c.totalReceived,
      'TDS Deducted (₹)': c.tdsDeducted,
      'Outstanding (₹)': c.outstanding,
      'Collection %': c.collectionPercent + '%'
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Top Clients');
    XLSX.writeFile(workbook, `Top_Clients_${dayjs().format('YYYY-MM-DD')}.xlsx`);
  };

  if (loading && !data) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin size="large" tip="Loading Analytics..." />
      </div>
    );
  }

  return (
    <div className="page-container" style={{ padding: '24px', background: '#f8fafc' }}>
      {/* Header & Filters */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <Title level={2} style={{ margin: 0, color: '#1e293b' }}>Dashboard</Title>
          <Text type="secondary">Consolidated financial overview and performance tracking</Text>
        </div>

        <Space size="middle" wrap>
          <Select
            placeholder="Select Facility"
            style={{ width: 200 }}
            allowClear
            value={filters.facility || undefined}
            onChange={val => setFilters(f => ({ ...f, facility: val || '' }))}
          >
            {facilities.map(f => (
              <Select.Option key={f.id} value={f.name}>{f.name}</Select.Option>
            ))}
          </Select>

          <Select
            value={filters.startDate}
            style={{ width: 180 }}
            onChange={val => {
              const start = dayjs(val);
              setFilters(f => ({ ...f, startDate: val, endDate: dayjs().format('YYYY-MM-DD') }));
            }}
          >
            <Select.Option value={dayjs().startOf('month').format('YYYY-MM-DD')}>This Month</Select.Option>
            <Select.Option value={dayjs().subtract(3, 'month').startOf('month').format('YYYY-MM-DD')}>Last 3 Months</Select.Option>
            <Select.Option value={dayjs().startOf('year').format('YYYY-MM-DD')}>Year to Date</Select.Option>
            <Select.Option value={dayjs().subtract(1, 'year').startOf('month').format('YYYY-MM-DD')}>Last 12 Months</Select.Option>
          </Select>
        </Space>
      </div>

      {/* KPI Cards */}
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} hoverable style={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ background: '#e0e7ff', padding: '10px', borderRadius: '10px' }}>
                <IndianRupee size={24} color="#4f46e5" />
              </div>
              <ArrowUpRight size={20} color="#22c55e" />
            </div>
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gross Revenue</Text>
              <Title level={3} style={{ margin: '4px 0 0 0' }}>{formatIndianCurrency(data?.summary?.revenue || 0, 0)}</Title>
              <Text type="secondary" style={{ fontSize: '12px' }}>{data?.summary?.invoiceCount || 0} Invoices generated</Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} hoverable style={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ background: '#dcfce7', padding: '10px', borderRadius: '10px' }}>
                <Wallet size={24} color="#16a34a" />
              </div>
              <Text style={{ color: '#16a34a', fontWeight: 600 }}>{collectionRate}% rate</Text>
            </div>
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Collection</Text>
              <Title level={3} style={{ margin: '4px 0 0 0' }}>{formatIndianCurrency(data?.summary?.collection || 0, 0)}</Title>
              <Text type="secondary" style={{ fontSize: '12px' }}>Actual cash received</Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} hoverable style={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ background: '#fee2e2', padding: '10px', borderRadius: '10px' }}>
                <TrendingDown size={24} color="#dc2626" />
              </div>
              <Download size={20} color="#dc2626" style={{ opacity: 0.3 }} />
            </div>
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Operational Spend</Text>
              <Title level={3} style={{ margin: '4px 0 0 0' }}>{formatIndianCurrency(data?.summary?.expenses || 0, 0)}</Title>
              <Text type="secondary" style={{ fontSize: '12px' }}>Opex + Recurring Costs</Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} hoverable style={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ background: '#fef3c7', padding: '10px', borderRadius: '10px' }}>
                <TrendingUp size={24} color="#d97706" />
              </div>
              <CheckCircle size={20} color="#d97706" style={{ opacity: 0.5 }} />
            </div>
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Operating Profit</Text>
              <Title level={3} style={{ margin: '4px 0 0 0' }}>{formatIndianCurrency(data?.summary?.netProfit || 0, 0)}</Title>
              <Text type="secondary" style={{ fontSize: '12px' }}>Pre-tax margin</Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Main Charts Row */}
      <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
        <Col xs={24} lg={16}>
          <Card
            title={<Space><BarChart3 size={18} /><span>Revenue vs Collection Trend</span></Space>}
            bordered={false}
            className="chart-card"
          >
            {trendChartData ? (
              <div style={{ height: '350px' }}>
                <Bar
                  data={trendChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'top', align: 'end' },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: { callback: (val) => formatIndianCurrency(val, 0) }
                      }
                    }
                  }}
                />
              </div>
            ) : <Empty />}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={<Space><Layers size={18} /><span>Invoice Breakdown</span></Space>}
            bordered={false}
            className="chart-card"
            style={{ height: '100%' }}
          >
            <div style={{ height: '280px' }}>
              <Doughnut
                data={getInvoiceTypeData()}
                options={{
                  maintainAspectRatio: false,
                  plugins: { legend: { position: 'bottom' } },
                  cutout: '65%'
                }}
              />
            </div>
            <Divider style={{ margin: '16px 0' }} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
              <Tag color="blue">{filteredInvoices.filter(i => i.invoiceType === 'rent').length} Rent</Tag>
              <Tag color="red">{filteredInvoices.filter(i => i.invoiceType === 'adhoc').length} Adhoc</Tag>
              <Tag color="orange">{filteredInvoices.filter(i => i.invoiceType === 'credit_note').length} Credit Notes</Tag>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Tables Row */}
      <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
        <Col xs={24} lg={14}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space><Users size={18} /><span>Top Paying Clients</span></Space>
                <Button size="small" variant="text" icon={<Download size={14} />} onClick={exportTopClientsToExcel}>Export</Button>
              </div>
            }
            bordered={false}
          >
            <Table
              dataSource={getTopClients().slice(0, 10)}
              pagination={false}
              size="small"
              rowKey="name"
              columns={[
                { title: 'Client', dataIndex: 'name', width: '40%' },
                { title: 'Received', dataIndex: 'totalReceived', align: 'right', render: v => formatIndianCurrency(v, 0) },
                { title: 'Outstanding', dataIndex: 'outstanding', align: 'right', render: v => <Text type={v > 0 ? 'danger' : 'success'}>{formatIndianCurrency(v, 0)}</Text> },
                {
                  title: 'Rate',
                  dataIndex: 'collectionPercent',
                  align: 'right',
                  render: v => <Tag color={v === 100 ? 'success' : v > 50 ? 'warning' : 'error'}>{v}%</Tag>
                }
              ]}
            />
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title={<Space><Calendar size={18} /><span>Monthly Summary</span></Space>} bordered={false}>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {data?.monthlyTrend?.slice()?.reverse()?.map((m, i) => (
                <div key={i} style={{
                  padding: '12px',
                  borderBottom: i === data.monthlyTrend.length - 1 ? 'none' : '1px solid #f1f5f9',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <div>
                    <Text strong>{MONTHS[m._id.month - 1]} {m._id.year}</Text>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{formatIndianCurrency(m.collection, 0)} collected</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <Text strong style={{ color: '#4f46e5' }}>{formatIndianCurrency(m.revenue, 0)}</Text>
                    <div style={{ fontSize: '10px' }}>
                      {m.revenue > 0 ? `${((m.collection / m.revenue) * 100).toFixed(0)}% recovery` : 'N/A'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      <style>{`
        .chart-card .ant-card-head { border-bottom: none; padding-top: 16px; }
        .ant-statistic-title { font-size: 11px; font-weight: 600; text-transform: uppercase; }
      `}</style>
    </div>
  );
};

export default Dashboard;